import { useMemo, useState } from "react";

import { formatDateTime } from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { Button, TST_COLUMN_SIZE, TSTTable } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { OAuthConnectionView } from "api/generated/model";

import { useOAuthConnectionsQuery } from "./queries/useOAuthConnectionsQuery";
import { useRevokeOAuthConnectionMutation } from "./queries/useRevokeOAuthConnectionMutation";
import { RevokeOAuthConnectionModal } from "./RevokeOAuthConnectionModal";

interface OAuthConnectionsTableProps {
    userId: number;
}

function applicationName(t: TFunction, connection: OAuthConnectionView) {
    return connection.clientName ?? t("OAuthConnections_UnknownApplication");
}

const columnHelper = createColumnHelper<OAuthConnectionView>();

export function OAuthConnectionsTable({ userId }: OAuthConnectionsTableProps) {
    const { t } = useTranslation();
    const { data: connections } = useOAuthConnectionsQuery(userId);
    const revoke = useRevokeOAuthConnectionMutation();
    const [revoking, setRevoking] = useState<OAuthConnectionView | null>(null);

    const columns = useMemo(
        () => [
            columnHelper.accessor((row) => applicationName(t, row), {
                id: "application",
                header: t("OAuthConnections_Column_Application"),
            }),
            columnHelper.accessor((row) => row.redirectHosts.join(", "), {
                id: "redirectHosts",
                header: t("OAuthConnections_Column_RedirectHosts"),
            }),
            columnHelper.accessor("lastIssuedAt", {
                header: t("OAuthConnections_Column_LastRefreshed"),
                cell: (info) => formatDateTime(info.getValue()),
                size: TST_COLUMN_SIZE.M,
            }),
            columnHelper.accessor("expiresAt", {
                header: t("OAuthConnections_Column_ExpiresAt"),
                cell: (info) => formatDateTime(info.getValue()),
                size: TST_COLUMN_SIZE.M,
            }),
            columnHelper.display({
                id: "revoke",
                header: "",
                cell: (info) => (
                    <Button
                        variant="link"
                        size="small"
                        danger
                        onClick={() => setRevoking(info.row.original)}
                    >
                        {t("OAuthConnections_Revoke")}
                    </Button>
                ),
                size: TST_COLUMN_SIZE.S,
            }),
        ],
        [t]
    );

    const table = useReactTable({
        data: connections,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: false,
        enableSorting: false,
    });

    const confirmRevoke = (connection: OAuthConnectionView) =>
        revoke.mutate(
            { userId, clientId: connection.clientId },
            { onSettled: () => setRevoking(null) }
        );

    return (
        <>
            {revoking && (
                <RevokeOAuthConnectionModal
                    applicationName={applicationName(t, revoking)}
                    loading={revoke.isPending}
                    onClose={() => setRevoking(null)}
                    onConfirm={() => confirmRevoke(revoking)}
                />
            )}
            <TSTTable
                table={table}
                noResultsFallback={t("OAuthConnections_Empty")}
                noSourceRowsFallback={t("OAuthConnections_Empty")}
            />
        </>
    );
}
